import React from "react";

import useSingleProjectCore from "../hooks/useSingleProjectCore";
import { Project } from "../types/project.type";

import { dateConverter } from "../../../utils/dateConverter";
import { limitString } from "../../../utils/stringUtils";
import StatusBox from "../../../components/atoms/StatusBox/StatusBox";

export type ProjectInfoObject = Project;

export interface ProjectInfoProps {
  info: keyof ProjectInfoObject;
  id?: string;
  styled?: boolean;
  emptyText?: string;
  limitText?: number;
  renderComponent?: (config: {
    labelRender: React.ReactNode;
    goTo: () => void;
  }) => JSX.Element;
}

const statusConverterProject = (status: string) => {
  const statusLower = String(status || "").toLowerCase();
  const isSuccess = ["done", "paid", "closed", "success", "completed"].includes(statusLower);
  const isWarning = ["pending", "in_progress", "processing"].includes(statusLower);
  const isError = ["cancelled", "failed", "rejected"].includes(statusLower);

  const color = isSuccess
    ? { background: "#2e7d32", text: "#ffffff" }
    : isWarning
    ? { background: "#ed6c02", text: "#ffffff" }
    : isError
    ? { background: "#d32f2f", text: "#ffffff" }
    : { background: "#0288d1", text: "#ffffff" };

  const size = { font: "12px", lineHeight: "18px", minHeight: "22px", height: "22px", width: "fit-content" };

  return <StatusBox label={status} color={color} size={size} />;
};

export default function ProjectInfo({
  info,
  id,
  emptyText,
  styled,
  limitText,
}: ProjectInfoProps) {
  const {
    querySingleProject: { isLoading, isError, data },
  } = useSingleProjectCore(id || "", null, null);

  const projectAttribute = info as keyof Project;

  if (isLoading) return <>...</>;
  if (isError) return <>{emptyText ? emptyText : ""}</>;

  const projectData = data as Project | undefined;

  if (info === "project_status" && styled && projectData?.project_status) {
    return statusConverterProject(projectData.project_status);
  }

  const convertValue = (field: keyof ProjectInfoObject, value: any) => {
    switch (field) {
      case "project_date":
      case "project_update_date":
      case "project_start_date":
        return dateConverter(value);
      case "project_close":
        return value ? "Oui" : "Non";
      default:
        return value;
    }
  };

  const value = projectData?.[projectAttribute] as any;

  return (
    <>
      {value !== undefined && value !== null && value !== ""
        ? limitString(String(convertValue(info, value)), limitText)
        : emptyText
        ? emptyText
        : ""}
    </>
  );
}


